<template>
  <div class="page-content">
    <ReportClipboard ref="clipboard" :report-data="formattedReportData" />
    <h1 class="pb-1"></h1>

    <!-- TABS SECTION -->
    <v-card class="mx-auto elevation-2 tabs-card">
      <v-container fluid>
        <v-row align="center" justify="space-between">
          <v-tabs v-model="activeTab" class="dashboard-tab">
            <v-tab 
              v-for="tab in tabs" 
              :key="tab.value" 
              :value="tab.value"
              @click="fetchReportData"
            >
              {{ tab.label }}
            </v-tab>
          </v-tabs>
        </v-row>
      </v-container>
    </v-card>

    <!-- SHIFT HEADER SECTION -->
    <v-card class="mt-4 p-3">
      <v-container>
        <v-row align="center">
          <!-- Shift Title (3 cols) -->
          <v-col cols="3" class="d-flex align-center justify-left">
            <h2 class="shift-title">{{ shiftTitle }}</h2>
          </v-col>

          <!-- Monitor Name (4 cols) -->
          <v-col cols="4">
            <v-combobox
              v-model="selectedMonitor"
              :items="monitorOptions"
              label="Monitor"
              variant="outlined"
              hide-details
            ></v-combobox>
          </v-col>

          <!-- Date Picker (4 cols) -->
          <v-col cols="4" class="d-flex align-center justify-center">
            <v-btn icon @click="changeDate(-1)" variant="text" style="color: #2C74B3;">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <v-date-input
              v-model="reportDate"
              label="Date"
              prepend-icon=""
              prepend-inner-icon=""
              hide-details
              variant="outlined"
              @update:model-value="fetchReportData"
            ></v-date-input>

            <v-btn icon @click="changeDate(1)" variant="text" style="color: #2C74B3;">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-col>

          <!-- Color Picker Button (1 col - moved to end) -->
          <v-col cols="1" class="d-flex align-center justify-end" style="margin-left: -18px;">
            <v-btn 
              icon 
              variant="outlined" 
              color="black"
              @click="colorPickerDialog = true"
              size="small"
            >
              <v-icon>mdi-brush</v-icon> 
            </v-btn>

            <!-- Hidden Color Picker Dialog -->
            <v-dialog v-model="colorPickerDialog" max-width="350px" persistent attach="body">
              <v-card>
                <v-card-title class="text-center">Pick a Header Color</v-card-title>
                <v-card-text>
                  <v-color-picker 
                    v-model="headerColor" 
                    hide-inputs
                    show-swatches
                  ></v-color-picker>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn @click="colorPickerDialog = false" color="primary">Done</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- MODEL ENTRY FORM -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="3">Model Name</v-col>
          <v-col cols="3">Stage Name</v-col>
          <v-col cols="2">Room #</v-col>
          <v-col cols="3">Assistance</v-col>
          <v-col cols="1" class="d-flex align-center justify-center">
            <v-btn 
              icon 
              variant="outlined" 
              color="white" 
              @click="addNewModel"
              size="compact"
              style="margin-left: 20px;"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Model Entry Form -->
        <v-form @submit.prevent>
          <v-row v-for="(model, index) in modelsList" :key="index" no-gutters>
            <!-- Model Name -->
            <v-col cols="3">
              <v-text-field v-model="model.name" variant="outlined" hide-details density="compact" @input="updateObservation(model)"></v-text-field>
            </v-col>

            <!-- Stage Name -->
            <v-col cols="3">
              <v-text-field v-model="model.stageName" variant="outlined" hide-details density="compact"></v-text-field>
            </v-col>

            <!-- Room Number (Dropdown) -->
            <v-col cols="2">
              <v-tooltip v-if="hasRoomConflict(model)" location="top">
                <template #activator="{ props }">
                  <v-select
                    v-model="model.room"
                    :items="roomOptions"
                    variant="outlined"
                    hide-details
                    density="compact"
                    clearable
                    v-bind="props"
                    :style="hasRoomConflict(model) ? 'box-shadow: inset 0 0 0 2px red;' : ''"
                  ></v-select>
                </template>
                <span>Multiple models in this room</span>
              </v-tooltip>
              <v-select
                v-else
                v-model="model.room"
                :items="roomOptions"
                variant="outlined"
                hide-details
                density="compact"
                clearable
              ></v-select>
            </v-col>

            <!-- Assistance Status (Dropdown) -->
            <v-col cols="3">
              <v-combobox v-model="model.assistance" :items="assistanceOptions" variant="outlined" hide-details density="compact"></v-combobox>
            </v-col>

            <!-- Remove Model Button -->
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-btn icon variant="outlined" color="black" @click="removeModel(index)" size="compact">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- TIME TRACKING SECTION -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="3">Model Name</v-col>
          <v-col cols="2.5" class="pl-0">Arrival Time</v-col>
          <v-col cols="2.5" class="pl-4">Connection Time</v-col>
          <v-col cols="2.5" class="pl-5">Log-Out Time</v-col>
          <v-col cols="1" class="text-center pr-4">Hours</v-col>
        </v-row>

        <v-divider class="mb-2"></v-divider>

        <!-- Time Entry Form -->
        <v-form @submit.prevent>
          <v-row v-for="(model, index) in timeTrackingModels" :key="index" no-gutters>
            <!-- Model Name -->
            <v-col cols="3">
              <v-text-field 
                v-model="model.name" 
                variant="outlined" 
                hide-details 
                readonly 
                density="compact"
              ></v-text-field>
            </v-col>

            <!-- Arrival Time -->
            <v-col cols="2.5">
              <TimePicker v-model="model.arrivalTime" />
            </v-col>

            <!-- Connection Time -->
            <v-col cols="2.5">
              <TimePicker v-model="model.connectionTime" />
            </v-col>

            <!-- Log-Out Time -->
            <v-col cols="2.5">
              <TimePicker v-model="model.logoutTime" />
            </v-col>

            <!-- Online Hours (Auto-Calculated) -->
            <v-col cols="1">
              <v-text-field 
                :value="calculateOnlineHours(model) + ' 🕒'" 
                variant="outlined"  
                hide-details
                single-line 
                density="compact"
                readonly
                style="text-align: left; padding-left: 0px !important;"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- EARNINGS ENTRY FORM -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="3">Model Name</v-col> 
          <v-col v-for="platform in platforms" :key="platform" cols="1.7" class="pl-2">{{ platform }}</v-col> 
          <v-col cols="1" class="text-center font-weight-bold pr-2">Total</v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Earnings Entry Form -->
        <v-form @submit.prevent>
          <v-row v-for="(model, index) in earningsModels" :key="model.name + '-' + index" no-gutters>
            <!-- Model Name (Auto-Filled from First Section) -->
            <v-col cols="3">
              <v-text-field 
                v-model="model.name" 
                variant="outlined" 
                hide-details 
                readonly
                density="compact"
              ></v-text-field>
            </v-col>

            <!-- Earnings Inputs for each platform -->
            <v-col v-for="platform in platforms" :key="platform" cols="1.7">
              <v-text-field 
                v-model="model.earnings[platform]"
                type="number"
                variant="outlined"
                hide-details
                @blur="formatEarnings(index, platform)" 
                @input="updateEarnings(index, platform, $event)"
                density="compact"
              ></v-text-field>
            </v-col>

            <!-- Row Total (With Background Color Based on Performance) -->
            <v-col cols="1" class="font-weight-bold d-flex align-center text-center justify-center">
              <span 
                :class="getEarningsClass(getRowTotal(model.earnings))" 
                class="total-earnings"
              >
                {{ formatCurrency(getRowTotal(model.earnings)) }}
              </span>
            </v-col>
          </v-row>

          <!-- Grand Total Row -->
          <v-divider class="mt-3 mb-2" style="border-color: black;"></v-divider>
          <v-row no-gutters>
            <v-col cols="3" class="font-weight-bold pl-2">Total Page Earnings</v-col>
            <v-col v-for="platform in platforms" :key="platform" cols="1.7" class="font-weight-bold pl-2">
              {{ formatCurrency(platformTotals[platform]) }}
            </v-col>
            <v-col cols="1" 
              class="font-weight-bold d-flex align-center justify-center text-center" 
              style="display: flex; align-items: center;"
            >
              <span 
                :class="getGrandTotalEarningsClass(grandTotal)" 
                class="total-earnings"
                style="margin-top: -8px;"
              >
                {{ formatCurrency(grandTotal) }}
              </span>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- ACTIONS & OBSERVATIONS SECTION -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="3">Model Focus</v-col>
          <v-col cols="9">Actions & Observations</v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Dynamic Row for Model Info & Observations -->
        <v-form @submit.prevent>
          <v-row v-for="(model, index) in modelsList" :key="index" no-gutters>
            <v-col cols="3" class="d-flex flex-column justify-space-between">
              <v-text-field 
              v-model="model.name"
              :label="`${model.stageName || '-' } - Room ${model.room || '-'}`"
              placeholder="Model Name"
              variant="outlined"
              readonly
              hide-details
              density="compact"
              style="padding-top: 10px;"
            ></v-text-field>
            </v-col>
            <v-col cols="9">
              <v-textarea 
                v-model="model.observations"
                variant="outlined"
                auto-grow
                rows="1"
                hide-details
                density="compact"
                style="padding-top: 10px;"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- BREAK & USAGE TRACKING -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col :style="{ flex: 3 }">Break</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Lush 1</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Lush 2</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Sex Machine</v-col>
          <v-col :style="{ flex: 2.25, marginLeft: '12px' }">Cellphone</v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Input Fields -->
        <v-row no-gutters>
          <!-- Break (Regular Input Field) -->
          <v-col cols="3">
            <v-text-field 
              v-model="breakEntry" 
              variant="outlined" 
              hide-details 
              density="compact"
            ></v-text-field>
          </v-col>

          <!-- Combobox Inputs for Usage Tracking -->
          <v-col v-for="(field, index) in ['lush1Entry', 'lush2Entry', 'sexMachineEntry', 'cellphoneEntry']" :key="index" cols="2.25">
            <v-combobox 
              v-model="usageEntries[field]" 
              :items="['Monitors Desk', ...modelsList.map(m => m.stageName || m.name)]" 
              variant="outlined" 
              hide-details 
              density="compact"
            ></v-combobox>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <!-- ADJUSTMENTS & PURCHASES SECTION -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col :style="{ flex: 3, textAlign: 'start' }">Adjustments</v-col>
          <v-col :style="{ flex: 1.5, textAlign: 'start' }">Type</v-col>
          <v-col :style="{ flex: 2.1, textAlign: 'start' }">Amount ($)</v-col>
          <v-col :style="{ flex: 3.6, textAlign: 'start' }">Description</v-col>
          <v-col :style="{ flex: 0.9, textAlign: 'start' }">Quotes</v-col>
          <v-col :style="{ flex: 1 }" class="d-flex align-center justify-center">
            <v-btn icon variant="outlined" color="white" @click="addAdjustmentRow" size="compact" style="margin-left: 22px;">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Dynamic Rows for Adjustments & Purchases -->
        <v-form @submit.prevent>
          <v-row v-for="(entry, index) in adjustmentList" :key="index" no-gutters>
            <v-col :style="{ flex: 3 }">
              <v-combobox v-model="entry.name" :items="modelNames" variant="outlined" hide-details density="compact"></v-combobox>
            </v-col>

            <v-col :style="{ flex: 1.5 }">
              <v-select v-model="entry.type" :items="['Adjustment', 'Discount', 'Purchase', 'Bonus']" variant="outlined" hide-details density="compact"></v-select>
            </v-col>

            <v-col :style="{ flex: 2 }" class="d-flex align-center">
              <v-text-field v-model.number="entry.amount" type="number" variant="outlined" hide-details density="compact">
                <template v-slot:append>
                  <span 
                    @click="toggleCurrency(entry)" 
                    :style="{ 
                      cursor: 'pointer', 
                      fontWeight: 'bold', 
                      color: entry.currency === 'USD'
                      ? '#27ae60'
                      : entry.currency === 'COP'
                      ? '#f39c12'
                      : '#2980b9',
                      paddingRight: '12px' 
                    }"
                  >
                    {{ entry.currency }}
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col :style="{ flex: 3.5 }">
              <v-text-field v-model="entry.description" variant="outlined" hide-details density="compact"></v-text-field>
            </v-col>

            <v-col :style="{ flex: 1 }">
              <v-select v-model="entry.quotes" :items="['1', '2', '3', '4', '5', '6']" variant="outlined" hide-details density="compact"></v-select>
            </v-col>

            <v-col :style="{ flex: 1 }" class="d-flex align-center justify-center">
              <v-btn icon variant="outlined" color="black" @click="removeAdjustmentRow(index)" size="compact">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- SHIFT OBSERVATIONS -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="10">Shift Observations</v-col>
          <v-col cols="2" class="d-flex justify-end">
            <v-btn 
              icon 
              variant="outlined" 
              color="white" 
              @click="addObservationRow"
              size="compact"
              style="margin-right: 26px;"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Shift Observations Dynamic Rows -->
        <v-form @submit.prevent>
          <v-row v-for="(observation, index) in observationsList" :key="index" no-gutters align="stretch">
            <v-col cols="11">
              <v-textarea 
                v-model="observationsList[index]"
                :ref="`observation-${index}`"
                variant="outlined"
                auto-grow
                rows="1"
                hide-details
                density="compact"
                class="w-100"
                persistent-placeholder
              ></v-textarea>
            </v-col>
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-btn 
                icon 
                variant="outlined" 
                color="black" 
                @click="removeObservationRow(index)"
                size="compact"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- CHECKLIST SECTION -->
    <v-container fluid class="pa-0">
      <v-card class="p-4 mt-4">
        <!-- Header Row -->
        <v-row no-gutters :style="headerStyle">
          <v-col cols="12">Checklist</v-col>
        </v-row>

        <v-divider class="mb-2" style="border-color: white;"></v-divider>

        <!-- Checklist in Two Columns -->
        <v-form @submit.prevent>
          <v-row v-for="(item, index) in checklistItems.slice(0, Math.ceil(checklistItems.length / 2))" :key="index" no-gutters class="mb-2">
            <!-- First Column -->
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-checkbox 
                v-model="checklistItems[index].checked" 
                density="compact"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col cols="5" class="d-flex align-center">
              {{ checklistItems[index].text }}
            </v-col>

            <!-- Second Column -->
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-checkbox 
                v-if="checklistItems[index + Math.ceil(checklistItems.length / 2)]"
                v-model="checklistItems[index + Math.ceil(checklistItems.length / 2)].checked"
                density="compact"
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col cols="5" class="d-flex align-center">
              <span v-if="checklistItems[index + Math.ceil(checklistItems.length / 2)]">
                {{ checklistItems[index + Math.ceil(checklistItems.length / 2)].text }}
              </span>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <!-- SAVE AND SEND BUTTONS -->
    <v-container fluid class="pa-0 mt-4">
      <v-row justify="end" no-gutters>
        <v-col cols="auto">
          <v-btn 
            color="success" 
            variant="elevated" 
            @click="handleCopyClick"
          >
            Copy
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import TimePicker from '@/components/TimePicker';
import ReportClipboard from '@/components/ReportClipboard.vue';
import secureAxios from '@/utils/secureAxios';
import { toast } from 'vue3-toastify';
import { debounce } from 'lodash';

export default {
  components: {
    TimePicker,
    ReportClipboard,
  },
  data() {
    return {
      activeTab: 'morning',
      tabs: [
        { value: 'morning', label: 'Morning' },
        { value: 'evening', label: 'Evening' },
        { value: 'night', label: 'Night' },
      ],
      monitorOptions: ['Danna Garcia', 'Frank Serrano', 'Sebas Torres'],
      defaultMonitors: {
        morning: 'Danna Garcia',
        evening: 'Frank Serrano',
        night: 'Sebas Torres',
      },
      selectedMonitor: 'Danna Garcia',
      monitorManuallyChanged: false,
      reportDate: new Date(),
      headerColor: '#000000',
      colorPickerDialog: false,
      observationsList: [''],
      roomOptions: ['1', '2', '3', '4', '5', '6', 'Other'],
      assistanceOptions: [
        'Yes',
        'No',
        'Day Off',
        'No (Offline Sales)',
        'Period Day',
        'Changed Shift',
        'Permission',
        'Vacation',
      ],
      platforms: ['Flirt4Free', 'Streamate', 'LiveJasmin', 'Chaturbate', 'StripChat'],
      modelsList: [],
      breakEntry: '',
      usageEntries: {
        lush1Entry: 'Monitors Desk',
        lush2Entry: 'Monitors Desk',
        sexMachineEntry: 'Monitors Desk',
        cellphoneEntry: 'Monitors Desk',
      },
      adjustmentList: [
        { name: '', type: 'Adjustment', amount: 0, currency: 'COP', description: '', quotes: '1' },
      ],
      checklistItems: [
        { text: 'Send Arrival Email', checked: false },
        { text: 'Check Rooms at Start of Shift', checked: false },
        { text: 'Review and Follow the Model Plan', checked: false },
        { text: 'Check Models’ Audio and Video', checked: false },
        { text: 'Log Adjustments: Inventory / Fines', checked: false },
        { text: 'Enter Sales in the Admin Center', checked: false },
        { text: 'Model Focus - Meeting with the Model', checked: false },
        { text: 'Delete RTMP Keys from OBS', checked: false },
        { text: 'Check Rooms and Equipment After Shift', checked: false },
        { text: 'Return Equipment (Lush, Mirrors, Machine, etc.)', checked: false },
        { text: 'Sign the Room Whiteboard', checked: false },
        { text: 'Send Daily Report', checked: false },
        { text: 'Send Exit Email (Include Adjusted Hours)', checked: false },
      ],
      fetchedOnce: false,
      unsavedChanges: false,
    };
  },
  computed: {
    shiftTitle() {
      return {
        morning: 'Morning Report',
        evening: 'Evening Report',
        night: 'Night Report',
      }[this.activeTab] || 'Night Shift';
    },
    headerStyle() {
      return {
        backgroundColor: this.headerColor,
        color: 'white',
        padding: '6px 12px',
        fontSize: '14px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        alignItems: 'center',
        height: '40px',
      };
    },
    timeTrackingModels() {
      return this.modelsList.filter((model) => model.assistance === 'Yes');
    },
    earningsModels() {
      return this.modelsList.filter(
        (model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)'
      );
    },
    modelFocusModels() {
      return this.modelsList.filter(
        (model) => model.observations && model.observations.trim() !== ''
      );
    },
    adjustmentModels() {
      return this.modelsList.filter(
        (model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)'
      );
    },
    platformTotals() {
      let totals = {};
      this.platforms.forEach((platform) => {
        totals[platform] = this.modelsList
          .filter((model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)')
          .reduce((sum, model) => sum + (Number(model.earnings?.[platform]) || 0), 0);
      });
      return totals;
    },
    grandTotal() {
      return this.modelsList
        .filter((model) => model.assistance === 'Yes' || model.assistance === 'No (Offline Sales)')
        .reduce((sum, model) => sum + this.getRowTotal(model.earnings), 0);
    },
    modelNames() {
      return this.modelsList.map((model) => model.name);
    },
    formattedReportData() {
      return this.getFormattedReportData();
    },
  },
  watch: {
    activeTab: {
      handler(newTab, oldTab) {
        if (newTab !== oldTab && !this.monitorManuallyChanged) {
          this.selectedMonitor = this.defaultMonitors[newTab] || 'Sebas Torres';
        }
      },
    },
    selectedMonitor: {
      handler() {
        this.monitorManuallyChanged = true;
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
    },
    reportDate: {
      handler(newDate, oldDate) {
        if (this.formatDate(newDate) !== this.formatDate(oldDate)) {
          this.fetchReportData(); // Trigger fetch on date change
          this.unsavedChanges = true;
          this.scheduleAutosave();
        }
      },
    },
    headerColor: {
      handler() {
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
    },
    observationsList: {
      handler() {
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    modelsList: {
      handler() {
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    breakEntry: {
      handler() {
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
    },
    usageEntries: {
      handler() {
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    adjustmentList: {
      handler() {
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
    checklistItems: {
      handler() {
        this.unsavedChanges = true;
        this.scheduleAutosave();
      },
      deep: true,
    },
  },
  methods: {
    async fetchActiveModels(shift) {
      try {
        console.log(`🌐 Sending request to /api/reports/models?shift=${shift}`);
        const response = await secureAxios.get('/api/reports/models', {
          params: { shift },
        });
        console.log(`✅ Received ${response.data.length} active models for ${shift}:`, response.data);
        return response.data;
      } catch (error) {
        console.error('❌ Error fetching active models:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
        toast.error('Failed to fetch active models');
        return [];
      }
    },
    fetchReportData: debounce(async function () {
      const date = this.formatDate(this.reportDate);
      const shift = this.activeTab.charAt(0).toUpperCase() + this.activeTab.slice(1);

      try {
        console.log(`🌐 Sending request to /api/reports?date=${date}&shift=${shift}`);
        const response = await secureAxios.get('/api/reports', {
          params: { date, shift },
        });
        const reportData = response.data;

        console.log(`📅 Fetched report for ${date} | Shift: ${shift}`);

        if (reportData) {
          Object.keys(reportData).forEach((prop) => {
            if (this.$data.hasOwnProperty(prop)) {
              if (prop === 'reportDate') {
                this[prop] = new Date(reportData[prop]);
              } else if (prop === 'modelsList') {
                this[prop] = reportData[prop].map((model) => ({
                  ...model,
                  arrivalTime: model.arrivalTime || this.getDefaultTimes().arrival,
                  connectionTime: model.connectionTime || this.getDefaultTimes().connection,
                  logoutTime: model.logoutTime || this.getDefaultTimes().logout,
                  earnings: model.earnings || this.getEmptyEarnings(),
                  assistance: model.assistance || '',
                  observations: model.observations || '',
                  stageName: model.stageName || '',
                  room: model.room || '',
                }));
              } else {
                this[prop] = reportData[prop];
              }
            }
          });
          console.log(`✅ Loaded report for ${date}_${shift}:`, reportData);
        } else {
          const activeModels = await this.fetchActiveModels(shift);
          const defaultTimes = this.getDefaultTimes();

          this.selectedMonitor = this.defaultMonitors[this.activeTab] || 'Sebas Torres';
          this.modelsList = activeModels.length > 0
            ? activeModels.map((model) => ({
                name: model.fullName || '',
                stageName: model.stageName || '',
                arrivalTime: defaultTimes.arrival,
                connectionTime: defaultTimes.connection,
                logoutTime: defaultTimes.logout,
                earnings: this.getEmptyEarnings(),
                assistance: '',
                observations: '',
                room: '',
              }))
            : [
                {
                  name: '',
                  stageName: '',
                  arrivalTime: defaultTimes.arrival,
                  connectionTime: defaultTimes.connection,
                  logoutTime: defaultTimes.logout,
                  earnings: this.getEmptyEarnings(),
                  assistance: '',
                  observations: '',
                  room: '',
                },
              ];
          this.breakEntry = '';
          this.usageEntries = {
            lush1Entry: 'Monitors Desk',
            lush2Entry: 'Monitors Desk',
            sexMachineEntry: 'Monitors Desk',
            cellphoneEntry: 'Monitors Desk',
          };
          this.adjustmentList = [
            { name: '', type: 'Adjustment', amount: 0, currency: 'COP', description: '', quotes: '1' },
          ];
          this.observationsList = [''];
          this.checklistItems = this.checklistItems.map((item) => ({ ...item, checked: false }));
          this.headerColor = '#000000';
          console.log(`ℹ️ No saved report for ${date}_${shift}, initialized with active models`);
        }
        this.unsavedChanges = false; // Reset after successful fetch
      } catch (error) {
        console.error('❌ Error fetching report:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
        toast.error('Failed to fetch report data');
      }
    }, 300),
    scheduleAutosave: debounce(function () {
      if (this.unsavedChanges) {
        this.saveReport(true);
      }
    }, 2000), // 2-second debounce for responsiveness
    async saveReport(autosave = false) {
      const date = this.formatDate(this.reportDate);
      const shift = this.activeTab;
      const reportData = {
        activeTab: this.activeTab,
        selectedMonitor: this.selectedMonitor,
        reportDate: this.reportDate.toISOString(),
        headerColor: this.headerColor,
        observationsList: this.observationsList,
        modelsList: this.modelsList,
        breakEntry: this.breakEntry,
        usageEntries: this.usageEntries,
        adjustmentList: this.adjustmentList,
        checklistItems: this.checklistItems,
      };
      try {
        console.log(`💾 Sending ${autosave ? 'autosave' : 'manual save'} request for ${date}_${shift}`);
        const response = await secureAxios.post('/api/reports/save', {
          date,
          shift,
          reportData,
        });
        console.log(
          `✅ ${autosave ? 'Autosave' : 'Report'} saved for ${date}_${shift}:`,
          response.data
        );
        if (!autosave) {
          toast.success(`Report saved for ${date} ${shift}`);
        }
        this.unsavedChanges = false;
      } catch (error) {
        console.error(`❌ Error ${autosave ? 'autosaving' : 'saving'} report:`, {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
        toast.error(`Failed to ${autosave ? 'autosave' : 'save'} report`);
      }
    },
    getDefaultTimes() {
      return {
        morning: {
          arrival: { hours: 5, minutes: 30, ampm: 'AM' },
          connection: { hours: 6, minutes: 0, ampm: 'AM' },
          logout: { hours: 1, minutes: 40, ampm: 'PM' },
        },
        evening: {
          arrival: { hours: 1, minutes: 30, ampm: 'PM' },
          connection: { hours: 2, minutes: 0, ampm: 'PM' },
          logout: { hours: 9, minutes: 40, ampm: 'PM' },
        },
        night: {
          arrival: { hours: 9, minutes: 30, ampm: 'PM' },
          connection: { hours: 10, minutes: 0, ampm: 'PM' },
          logout: { hours: 5, minutes: 40, ampm: 'AM' },
        },
      }[this.activeTab] || {
        arrival: { hours: 9, minutes: 30, ampm: 'PM' },
        connection: { hours: 10, minutes: 0, ampm: 'PM' },
        logout: { hours: 5, minutes: 40, ampm: 'AM' },
      };
    },
    addNewModel() {
      const defaultTimes = this.getDefaultTimes();
      this.modelsList.push({
        name: '',
        stageName: '',
        arrivalTime: { ...defaultTimes.arrival },
        connectionTime: { ...defaultTimes.connection },
        logoutTime: { ...defaultTimes.logout },
        earnings: this.getEmptyEarnings(),
        assistance: '',
        observations: '',
        room: '',
      });
    },
    removeModel(index) {
      this.modelsList.splice(index, 1);
    },
    toggleCurrency(entry) {
      const currencies = ['EUR', 'COP', 'USD'];
      const currentIndex = currencies.indexOf(entry.currency);
      entry.currency = currencies[(currentIndex + 1) % currencies.length];
    },
    getEarningsClass(amount) {
      if (amount <= 24.99) return 'red-bg';
      else if (amount >= 25 && amount < 50) return 'yellow-bg';
      else if (amount >= 50) return 'green-bg';
      return '';
    },
    getGrandTotalEarningsClass() {
      const relevantModels = this.modelsList.filter((model) => model.assistance === 'Yes');
      const numModels = relevantModels.length;
      if (numModels === 0) return '';
      const redThreshold = 24.99 * numModels;
      const yellowThreshold = 49.99 * numModels;
      const greenThreshold = 50 * numModels;
      const adjustedTotal = this.grandTotal;
      if (adjustedTotal <= redThreshold) return 'red-bg';
      else if (adjustedTotal > redThreshold && adjustedTotal < yellowThreshold) return 'yellow-bg';
      else if (adjustedTotal >= greenThreshold) return 'green-bg';
      return '';
    },
    calculateOnlineHours(model) {
      if (!model.connectionTime || !model.logoutTime) return '00:00';
      const convertTo24hr = (time) => {
        let hours = time.hours;
        if (time.ampm === 'PM' && time.hours !== 12) hours += 12;
        if (time.ampm === 'AM' && time.hours === 12) hours = 0;
        return { hours, minutes: time.minutes };
      };
      const connection = convertTo24hr(model.connectionTime);
      const logout = convertTo24hr(model.logoutTime);
      let connectionDate = new Date(0, 0, 0, connection.hours, connection.minutes);
      let logoutDate = new Date(0, 0, 0, logout.hours, logout.minutes);
      if (logoutDate < connectionDate) logoutDate.setDate(logoutDate.getDate() + 1);
      let diffMs = logoutDate - connectionDate;
      let diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      let diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHours.toString().padStart(2, '0')}:${diffMinutes.toString().padStart(2, '0')}`;
    },
    changeDate(days) {
      let currentDate = new Date(this.reportDate);
      currentDate.setDate(currentDate.getDate() + days);
      this.reportDate = currentDate;
    },
    initializeObservation(model) {
      if (!model.observations || model.observations.trim() === '') {
        model.observations = `${model.name} - `;
      } else if (!model.observations.startsWith(`${model.name} - `)) {
        model.observations = `${model.name} - ${model.observations}`;
      }
    },
    
    addObservationRow() {
      this.observationsList.push('');
      this.$nextTick(() => {
        const index = this.observationsList.length - 1;
        const ref = this.$refs[`observation-${index}`];
        if (ref && ref[0] && typeof ref[0].focus === 'function') {
          ref[0].focus();
        }
      });
    },
    removeObservationRow(index) {
      if (this.observationsList.length > 1) this.observationsList.splice(index, 1);
    },
    getEmptyEarnings() {
      return this.platforms.reduce((acc, platform) => {
        acc[platform] = 0;
        return acc;
      }, {});
    },
    formatDate(date) {
      const d = date instanceof Date ? date : new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatCurrency(value) {
      if (isNaN(value) || value === '') return '$0.00';
      return value < 0 ? `-${Math.abs(value).toFixed(2)}` : `$${parseFloat(value).toFixed(2)}`;
    },
    updateEarnings(index, platform, event) {
      let rawValue = event.target.value;
      rawValue = rawValue.replace(/[^0-9.-]/g, '');
      if (rawValue.indexOf('-') !== -1) {
        const minusIndex = rawValue.indexOf('-');
        rawValue =
          '-' + rawValue.slice(0, minusIndex).replace('-', '') + rawValue.slice(minusIndex + 1).replace(/-/g, '');
      }
      if (rawValue === '-' || rawValue === '') {
        this.modelsList[index].earnings[platform] = rawValue;
        return;
      }
      const decimalCount = (rawValue.match(/\./g) || []).length;
      if (decimalCount > 1) {
        const firstDecimalIndex = rawValue.indexOf('.');
        rawValue = rawValue.slice(0, firstDecimalIndex + 1) + rawValue.slice(firstDecimalIndex + 1).replace(/\./g, '');
      }
      if (rawValue.match(/^(-?)0+([1-9])/)) {
        rawValue = rawValue.replace(/^(-?)0+([1-9])/, '$1$2');
      }
      this.modelsList[index].earnings[platform] = rawValue;
    },
    formatEarnings(index, platform) {
      let value = this.modelsList[index].earnings[platform];
      if (value !== '' && !isNaN(value)) {
        this.modelsList[index].earnings[platform] = parseFloat(value).toFixed(2);
      }
    },
    getRowTotal(earnings) {
      return Object.values(earnings).reduce((sum, value) => sum + (Number(value) || 0), 0);
    },
    addAdjustmentRow() {
      this.adjustmentList.push({
        name: '',
        type: 'Adjustment',
        amount: 0,
        currency: 'COP',
        description: '',
        quotes: '1',
      });
    },
    removeAdjustmentRow(index) {
      this.adjustmentList.splice(index, 1);
    },
    sendReport() {
      const reportData = {
        activeTab: this.activeTab,
        selectedMonitor: this.selectedMonitor,
        reportDate: this.formatDate(this.reportDate),
        modelsList: this.modelsList,
        breakEntry: this.breakEntry,
        usageEntries: this.usageEntries,
        adjustmentList: this.adjustmentList,
        observationsList: this.observationsList,
        checklistItems: this.checklistItems,
        grandTotal: this.grandTotal,
        platformTotals: this.platformTotals,
      };
      console.log('📤 Sending report:', reportData);
      toast.info('Send report functionality not yet implemented');
    },
    getFormattedReportData() {
    const formatTime = (timeObj) => {
      if (!timeObj || !('hours' in timeObj) || !('minutes' in timeObj) || !('ampm' in timeObj)) return '-';
      return `${timeObj.hours}:${timeObj.minutes.toString().padStart(2, '0')} ${timeObj.ampm}`;
    };

    // Only include models whose assistance is marked as "Yes" for time tracking
    const allModelsWithTime = this.modelsList
      .filter(model => model.assistance && model.assistance.toLowerCase() === 'yes')
      .map(model => ({
        fullName: model.name,
        arrival: formatTime(model.arrivalTime),
        connection: formatTime(model.connectionTime),
        logout: formatTime(model.logoutTime),
        hours: this.calculateOnlineHours(model)
      }));

    // Sales data should include models with assistance "Yes" or "No (Offline Sales)"
    const salesData = this.earningsModels.map(model => {
      const timeData = allModelsWithTime.find(m => m.fullName === model.name) || {};
      return {
        fullName: model.name,
        flirt4free: model.earnings.Flirt4Free || 0,
        streamate: model.earnings.Streamate || 0,
        livejasmin: model.earnings.LiveJasmin || 0,
        chaturbate: model.earnings.Chaturbate || 0,
        stripchat: model.earnings.StripChat || 0,
        total: this.getRowTotal(model.earnings),
        arrival: timeData.arrival || '-',
        connection: timeData.connection || '-',
        logout: timeData.logout || '-',
        hours: timeData.hours || '-',
        goalColor: this.getEarningsClass(this.getRowTotal(model.earnings))
      };
    });

    return {
      shift: this.activeTab,
      date: this.formatDate(this.reportDate),
      monitor: this.selectedMonitor,
      headerColor: this.headerColor,
      attendance: this.modelsList.map(model => ({
        fullName: model.name,
        stageName: model.stageName || '-',
        room: model.room || '-',
        assistance: model.assistance || '-'
      })),
      salesData: salesData,
      modelFocus: this.modelFocusModels.map(model => ({
        focus: model.name,
        notes: model.observations || '-',
        stageName: model.stageName || '-',
        room: model.room || '-'
      })),
      breakItems: [{
        break: this.breakEntry || '-',
        lush1: this.usageEntries.lush1Entry || '-',
        lush2: this.usageEntries.lush2Entry || '-',
        machine: this.usageEntries.sexMachineEntry || '-',
        cellphone: this.usageEntries.cellphoneEntry || '-'
      }],
      adjustmentList: this.adjustmentList.map(adj => ({
        name: adj.name || '-',
        type: adj.type || '-',
        amount: adj.amount || 0,
        currency: adj.currency || 'COP',
        description: adj.description || '-',
        quotas: adj.quotes || '-'
      })),
      shiftObservations: this.observationsList.filter(obs => obs.trim() !== ''),
      checklist: this.checklistItems.map(item => `${item.checked ? '✅ ' : '❌ '} ${item.text}`),
      platformTotals: this.platformTotals,
      grandTotal: this.grandTotal
    };
  },
    handleBeforeUnload(event) {
      if (this.unsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    onCopySuccess() {
      toast.success('Report copied to clipboard!');
    },
    onCopyFailed(err) {
      console.error('Copy failed:', err);
      toast.error('Failed to copy report');
    },
    hasRoomConflict(model) {
      if (model.assistance !== 'Yes' || !model.room) return false;
      const modelsInSameRoom = this.modelsList.filter(
        (m) => m !== model && m.assistance === 'Yes' && m.room === model.room
      );
      return modelsInSameRoom.length > 0;
    },
    handleCopyClick() {
      this.$refs.clipboard?.copyToClipboard?.();
      toast.success('Report copied to clipboard!');
    },
  },
  mounted() {
    if (!this.fetchedOnce) {
      this.fetchReportData();
      this.fetchedOnce = true;
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.unsavedChanges) {
      this.saveReport(true);
    }
  },
  
  
};
</script>

<style scoped>
.dashboard-tab .v-tab {
  background-color: white;
  color: #2C74B3;
}

.shift-title {
  font-size: 30px;
  font-weight: bold;
  color: black;
  text-align: center;
}

.total-earnings {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 5px;
  color: rgb(0, 0, 0);
  text-align: center;
  min-width: 70px;
}

.red-bg {
  background-color: #e74d3c3c;
}

.yellow-bg {
  background-color: #f1c40f58;
  color: black;
}

.green-bg {
  background-color: #2ecc7051;
}


</style>
